import { useState, useEffect } from 'react';
import { ArrowLeft, Bell, Volume2, Vibrate, MapPin, Shield, Eye, HelpCircle, FileText, LogOut, ChevronRight } from 'lucide-react';
import { auth } from '../../config/firebase.config';
import { signOut } from 'firebase/auth';
import { dbService } from '../../services/firebase-db.service';
import { soundService } from '../../services/sound.service';

interface SettingsScreenProps {
  onBack: () => void;
}

export function SettingsScreen({ onBack }: SettingsScreenProps) {
  const [pushNotifications, setPushNotifications] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [vibrationEnabled, setVibrationEnabled] = useState(true);
  const [locationAlways, setLocationAlways] = useState(true);

  // Load saved settings from profile on mount
  useEffect(() => {
    const loadSettings = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const profile = await dbService.getRiderProfile(user.uid);
        // Keep the sound engine in step with the rider's saved preference.
        soundService.syncFromProfile(profile?.settings);
        if (profile?.settings) {
          const s = profile.settings;
          if (s.pushNotifications !== undefined) setPushNotifications(s.pushNotifications);
          if (s.soundEnabled !== undefined) setSoundEnabled(s.soundEnabled);
          if (s.vibrationEnabled !== undefined) setVibrationEnabled(s.vibrationEnabled);
          if (s.locationAlways !== undefined) setLocationAlways(s.locationAlways);
        }
      } catch (_) {}
    };
    loadSettings();
  }, []);

  const saveSettings = async (updates: Record<string, boolean>) => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await dbService.updateRiderProfile(user.uid, { settings: updates } as any);
    } catch (err) {
      console.error('Failed to save settings:', err);
    }
  };

  const handleToggle = (key: string, value: boolean) => {
    const allSettings = {
      pushNotifications,
      soundEnabled,
      vibrationEnabled,
      locationAlways,
      [key]: value,
    };
    switch (key) {
      case 'pushNotifications': setPushNotifications(value); break;
      case 'soundEnabled': setSoundEnabled(value); soundService.setEnabled(value); break;
      case 'vibrationEnabled': setVibrationEnabled(value); break;
      case 'locationAlways': setLocationAlways(value); break;
    }
    saveSettings(allSettings);
  };

  const SettingToggle = ({ 
    enabled, 
    onChange 
  }: { 
    enabled: boolean; 
    onChange: (value: boolean) => void 
  }) => (
    <button
      onClick={() => onChange(!enabled)}
      className={`relative w-14 h-12 rounded-full transition-colors ${
        enabled ? 'bg-[#00B277]' : 'bg-gray-300'
      }`}
    >
      <div
        className={`absolute top-1 w-10 h-10 rounded-full bg-white shadow-md transition-transform ${
          enabled ? 'translate-x-3' : 'translate-x-1'
        }`}
      />
    </button>
  );

  return (
    <div className="absolute inset-0 bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button 
          onClick={onBack}
          className="w-12 h-12 flex items-center justify-center hover:bg-gray-100 rounded-xl transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft className="w-6 h-6 text-[#111111]" />
        </button>
        <h1 className="text-[18px] text-[#111111]">Settings</h1>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {/* Notifications Section */}
        <div className="bg-white mt-2 px-4 py-3">
          <h2 className="text-[12px] text-[#6B6B6B] mb-3">NOTIFICATIONS</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#E0001B]/10 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-[#E0001B]" />
                </div>
                <div>
                  <p className="text-[14px] text-[#111111]">Push Notifications</p>
                  <p className="text-[12px] text-[#6B6B6B]">Receive job alerts</p>
                </div>
              </div>
              <SettingToggle 
                enabled={pushNotifications}
                onChange={(v) => handleToggle('pushNotifications', v)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#E0001B]/10 flex items-center justify-center">
                  <Volume2 className="w-5 h-5 text-[#E0001B]" />
                </div>
                <div>
                  <p className="text-[14px] text-[#111111]">Sound</p>
                  <p className="text-[12px] text-[#6B6B6B]">Alert sounds</p>
                </div>
              </div>
              <SettingToggle 
                enabled={soundEnabled}
                onChange={(v) => handleToggle('soundEnabled', v)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#E0001B]/10 flex items-center justify-center">
                  <Vibrate className="w-5 h-5 text-[#E0001B]" />
                </div>
                <div>
                  <p className="text-[14px] text-[#111111]">Vibration</p>
                  <p className="text-[12px] text-[#6B6B6B]">Haptic feedback</p>
                </div>
              </div>
              <SettingToggle 
                enabled={vibrationEnabled}
                onChange={(v) => handleToggle('vibrationEnabled', v)}
              />
            </div>
          </div>
        </div>

        {/* Privacy & Permissions */}
        <div className="bg-white mt-2 px-4 py-3">
          <h2 className="text-[12px] text-[#6B6B6B] mb-3">PRIVACY & PERMISSIONS</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#00B277]/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#00B277]" />
                </div>
                <div>
                  <p className="text-[14px] text-[#111111]">Location Access</p>
                  <p className="text-[12px] text-[#6B6B6B]">Always allow</p>
                </div>
              </div>
              <SettingToggle 
                enabled={locationAlways}
                onChange={(v) => handleToggle('locationAlways', v)}
              />
            </div>

            <button className="w-full flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#00B277]/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#00B277]" />
                </div>
                <div className="text-left">
                  <p className="text-[14px] text-[#111111]">Data & Privacy</p>
                  <p className="text-[12px] text-[#6B6B6B]">Manage your data</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-[#6B6B6B]" />
            </button>

            <button className="w-full flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#00B277]/10 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-[#00B277]" />
                </div>
                <div className="text-left">
                  <p className="text-[14px] text-[#111111]">Privacy Policy</p>
                  <p className="text-[12px] text-[#6B6B6B]">View policy</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-[#6B6B6B]" />
            </button>
          </div>
        </div>

        {/* Support Section */}
        <div className="bg-white mt-2 px-4 py-3">
          <h2 className="text-[12px] text-[#6B6B6B] mb-3">SUPPORT</h2>
          
          <div className="space-y-4">
            <button className="w-full flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="text-[14px] text-[#111111]">Help Center</p>
                  <p className="text-[12px] text-[#6B6B6B]">FAQs and guides</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-[#6B6B6B]" />
            </button>

            <button className="w-full flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="text-[14px] text-[#111111]">Terms of Service</p>
                  <p className="text-[12px] text-[#6B6B6B]">Legal agreements</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-[#6B6B6B]" />
            </button>
          </div>
        </div>

        {/* App Info */}
        <div className="bg-white mt-2 px-4 py-3">
          <h2 className="text-[12px] text-[#6B6B6B] mb-3">APP INFO</h2>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[14px] text-[#6B6B6B]">Version</span>
              <span className="text-[14px] text-[#111111]">2.4.1</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[14px] text-[#6B6B6B]">Build</span>
              <span className="text-[14px] text-[#111111]">20251208</span>
            </div>
          </div>
        </div>

        {/* Logout */}
        <div className="bg-white mt-2 px-4 py-3 mb-4">
          <button
            onClick={async () => {
              if (confirm('Are you sure you want to log out?')) {
                try {
                  await signOut(auth);
                  window.location.reload();
                } catch (err) {
                  console.error('Logout failed:', err);
                }
              }
            }}
            className="w-full flex items-center justify-center gap-2 h-12 rounded-[12px] bg-[#E02020]/10 text-[#E02020] hover:bg-[#E02020]/20 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-[16px]">Log Out</span>
          </button>
        </div>
      </div>
    </div>
  );
}